import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  useTheme,
  useMediaQuery,
  Collapse,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// 아이콘
import DashboardIcon from '@mui/icons-material/Dashboard';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

const Sidebar = ({ open, onClose, collapsed, onToggleCollapse, onTabOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  
  const [dataOpen, setDataOpen] = useState(location.pathname.includes('/data'));
  
  const handleDataClick = () => {
    setDataOpen(!dataOpen);
  };
  
  // 탭 시스템과 연동된 네비게이션 핸들러
  const handleNavigation = (path) => {
    // 탭 시스템에 새 탭 열기 요청
    if (onTabOpen) {
      onTabOpen(path);
    }
    
    // 라우터 네비게이션 (URL 변경)
    navigate(path);
    
    // 모바일에서는 사이드바 닫기
    if (isMobile) {
      onClose();
    }
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ 
      overflow: 'auto', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, rgba(5, 19, 54, 0.95) 0%, rgba(10, 36, 99, 0.95) 100%)',
      backdropFilter: 'blur(10px)',
      mt: 2.5,
    }}>
      <Box sx={{ 
        p: collapsed ? 1 : 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: collapsed ? 'center' : 'space-between',
        borderBottom: '1px solid rgba(76, 185, 231, 0.3)'
      }}>
        {!collapsed && (
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'primary.main', 
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
            }}
          >
          </Typography>
        )}
        
        {!isMobile && (
          <IconButton 
            onClick={onToggleCollapse}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.1)',
                boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
              }
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>
      
      <List sx={{ flex: 1 }}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "대시보드" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={() => handleNavigation('/dashboard')}
              selected={isActive('/dashboard')}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                },
                '&.Mui-selected:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                },
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive('/dashboard') ? 'primary.main' : 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  filter: isActive('/dashboard') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                }}
              >
                <DashboardIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="대시보드" 
                  primaryTypographyProps={{ 
                    color: isActive('/dashboard') ? 'primary.main' : 'text.primary',
                    fontWeight: isActive('/dashboard') ? 500 : 400,
                    textShadow: isActive('/dashboard') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                    sx: {
                      transition: 'all 0.3s ease',
                    }
                  }} 
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "활동 데이터" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={handleDataClick}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                <DataUsageIcon />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText primary="활동 데이터" />
                  {dataOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        {(!collapsed || (collapsed && dataOpen)) && (
          <Collapse in={dataOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem disablePadding>
                <Tooltip title={collapsed ? "데이터 입력" : ""} placement="right" arrow>
                  <ListItemButton 
                    sx={{ 
                      pl: collapsed ? 2.5 : 4,
                      minHeight: 48,
                      justifyContent: collapsed ? 'center' : 'initial',
                      '&.Mui-selected': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                      },
                      '&.Mui-selected:hover': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                      },
                      '&:hover': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                      },
                    }} 
                    onClick={() => handleNavigation('/data/input')}
                    selected={isActive('/data/input')}
                  >
                    <ListItemIcon 
                      sx={{ 
                        color: isActive('/data/input') ? 'primary.main' : 'text.secondary',
                        minWidth: collapsed ? 0 : 40,
                        mr: collapsed ? 0 : 2,
                        justifyContent: 'center',
                        filter: isActive('/data/input') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                      }}
                    >
                      <AddCircleOutlineIcon />
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText 
                        primary="데이터 입력" 
                        primaryTypographyProps={{ 
                          color: isActive('/data/input') ? 'primary.main' : 'text.primary',
                          fontWeight: isActive('/data/input') ? 500 : 400,
                          textShadow: isActive('/data/input') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                          sx: {
                            transition: 'all 0.3s ease',
                          }
                        }} 
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              
              <ListItem disablePadding>
                <Tooltip title={collapsed ? "데이터 조회" : ""} placement="right" arrow>
                  <ListItemButton 
                    sx={{ 
                      pl: collapsed ? 2.5 : 4,
                      minHeight: 48,
                      justifyContent: collapsed ? 'center' : 'initial',
                      '&.Mui-selected': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                      },
                      '&.Mui-selected:hover': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                      },
                      '&:hover': {
                        background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                      },
                    }} 
                    onClick={() => handleNavigation('/data/history')}
                    selected={isActive('/data/history')}
                  >
                    <ListItemIcon 
                      sx={{ 
                        color: isActive('/data/history') ? 'primary.main' : 'text.secondary',
                        minWidth: collapsed ? 0 : 40,
                        mr: collapsed ? 0 : 2,
                        justifyContent: 'center',
                        filter: isActive('/data/history') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                      }}
                    >
                      <ListAltIcon />
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText 
                        primary="데이터 조회" 
                        primaryTypographyProps={{ 
                          color: isActive('/data/history') ? 'primary.main' : 'text.primary',
                          fontWeight: isActive('/data/history') ? 500 : 400,
                          textShadow: isActive('/data/history') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                          sx: {
                            transition: 'all 0.3s ease',
                          }
                        }} 
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </List>
          </Collapse>
        )}
        
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "예측 분석" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={() => handleNavigation('/predictions')}
              selected={isActive('/predictions')}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                },
                '&.Mui-selected:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                },
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive('/predictions') ? 'primary.main' : 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  filter: isActive('/predictions') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                }}
              >
                <TimelineIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="예측 분석" 
                  primaryTypographyProps={{ 
                    color: isActive('/predictions') ? 'primary.main' : 'text.primary',
                    fontWeight: isActive('/predictions') ? 500 : 400,
                    textShadow: isActive('/predictions') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                    sx: {
                      transition: 'all 0.3s ease',
                    }
                  }} 
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "보고서" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={() => handleNavigation('/reports')}
              selected={isActive('/reports')}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                },
                '&.Mui-selected:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                },
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive('/reports') ? 'primary.main' : 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  filter: isActive('/reports') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                }}
              >
                <AssessmentIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="보고서" 
                  primaryTypographyProps={{ 
                    color: isActive('/reports') ? 'primary.main' : 'text.primary',
                    fontWeight: isActive('/reports') ? 500 : 400,
                    textShadow: isActive('/reports') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                    sx: {
                      transition: 'all 0.3s ease',
                    }
                  }} 
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        <Divider sx={{ my: 2, borderColor: 'rgba(76, 185, 231, 0.2)' }} />
        
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "설정" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={() => handleNavigation('/settings')}
              selected={isActive('/settings')}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                },
                '&.Mui-selected:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                },
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive('/settings') ? 'primary.main' : 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  filter: isActive('/settings') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="설정" 
                  primaryTypographyProps={{ 
                    color: isActive('/settings') ? 'primary.main' : 'text.primary',
                    fontWeight: isActive('/settings') ? 500 : 400,
                    textShadow: isActive('/settings') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                    sx: {
                      transition: 'all 0.3s ease',
                    }
                  }} 
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "도움말" : ""} placement="right" arrow>
            <ListItemButton 
              onClick={() => handleNavigation('/help')}
              selected={isActive('/help')}
              sx={{
                minHeight: 48,
                px: 2.5,
                justifyContent: collapsed ? 'center' : 'initial',
                '&.Mui-selected': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.15) 0%, rgba(10, 36, 99, 0.5) 100%)',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'inset 0 0 10px rgba(0, 229, 255, 0.1)',
                },
                '&.Mui-selected:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.2) 0%, rgba(10, 36, 99, 0.6) 100%)',
                },
                '&:hover': {
                  background: 'linear-gradient(90deg, rgba(0, 229, 255, 0.1) 0%, rgba(10, 36, 99, 0.4) 100%)',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isActive('/help') ? 'primary.main' : 'text.secondary',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  filter: isActive('/help') ? 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))' : 'none',
                }}
              >
                <HelpOutlineIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="도움말" 
                  primaryTypographyProps={{ 
                    color: isActive('/help') ? 'primary.main' : 'text.primary',
                    fontWeight: isActive('/help') ? 500 : 400,
                    textShadow: isActive('/help') ? '0 0 5px rgba(0, 229, 255, 0.5)' : 'none',
                    sx: {
                      transition: 'all 0.3s ease',
                    }
                  }} 
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? (collapsed ? collapsedDrawerWidth : drawerWidth) : 0 },
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        position: 'relative',
        zIndex: 20,
        borderRadius: 0
      }}
    >
      {/* 모바일 드로어 */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // 모바일 성능 향상
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(76, 185, 231, 0.3)',
              boxShadow: '4px 0 20px rgba(0, 0, 0, 0.5)',
              borderRadius: 0
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
      
      {/* 데스크톱 드로어 */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            borderRight: '1px solid rgba(76, 185, 231, 0.3)',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.5)',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            borderRadius: 0
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
